import i18next from "i18next";
import Backend from "i18next-http-backend";

/** Locales under `public/locales/`; the first is the fallback. */
export const LANGUAGES = ["en-US", "es"] as const;

/**
 * The first browser language whose primary subtag we ship (`es-MX` → `es`),
 * else English. No switcher yet: Spanish follows the browser's language list.
 */
export const pickLanguage = (preferred: ReadonlyArray<string>): string =>
	preferred
		.map((tag) => tag.toLowerCase().split("-")[0])
		.flatMap((base) => LANGUAGES.filter((lng) => lng.split("-")[0] === base))
		.at(0) ?? LANGUAGES[0];

const options = {
	fallbackLng: LANGUAGES[0],
	supportedLngs: LANGUAGES,
	defaultNS: "common",
	ns: ["common"],
	interpolation: { escapeValue: false },
} as const;

/** Loads `public/locales/<lng>/common.json` for the browser's language (plus the English fallback; keys checked by `npm run i18n:check`). */
export const initTranslations = (): Promise<boolean> =>
	i18next
		.use(Backend)
		.init({
			...options,
			lng: pickLanguage(navigator.languages ?? [navigator.language]),
			backend: { loadPath: "/locales/{{lng}}/{{ns}}.json" },
		})
		.then(
			() => {
				document.documentElement.lang = i18next.language;
				return true;
			},
			() => false,
		);

/** For tests: load the bundle directly, no HTTP. */
export const initTranslationsFrom = (common: object): Promise<boolean> =>
	i18next
		.init({ ...options, lng: LANGUAGES[0], resources: { "en-US": { common } } })
		.then(() => true);

/**
 * Subtype labels. The ids (`person`, `note`, …) are wire values and never change;
 * only their labels do, and they live in `record.subtype.*` / `entry.subtype.*`.
 */
export const recordSubtypeLabel = (type: string): string =>
	i18next.t(`record.subtype.${type}`, { defaultValue: type });

export const entrySubtypeLabel = (type: string): string =>
	i18next.t(`entry.subtype.${type}`, { defaultValue: type });

/** The add-entry menu's option groups, in order; the first element is a translation key. */
export const ENTRY_SUBTYPE_GROUPS: ReadonlyArray<
	readonly [string, ReadonlyArray<string>]
> = [
	["entry.groupJournal", ["note", "update", "learning", "accomplishment"]],
	["entry.groupPlanning", ["decision", "risk", "hypothesis", "experiment"]],
	["entry.groupArtifacts", ["tasks", "upload"]],
];

export { i18next };
